import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_CAPABILITIES,
  GPX_FEATURES,
  GPX_ROUTES,
} from '@rsos/gainsight';

// Event names specific to VT911
const VT911_EVENT_NAMES = {
  START: 'start_video_to_911',
  START_RESPONSE: 'start_vt911_response',
  END: 'end_vt911',
  END_RESPONSE: 'end_vt911_response',
  END_FROM_SERVER: 'end_vt911_from_server',
  BLUR_VIDEO: 'blur_video',
  UNBLUR_VIDEO: 'unblur_video',
  SHOW_FULLSCREEN: 'show_fullscreen',
  HIDE_FULLSCREEN: 'hide_fullscreen',
  MUTE_VIDEO: 'mute_video',
  UNMUTE_VIDEO: 'unmute_video',
  HIDE_VIDEO_CARD: 'hide_video_card',
};

/**
 * Tracks when a user clicks to start a Video to 911 session
 * @param {Object} params - The parameters for tracking the video session initiation
 * @param {boolean} params.fromOverviewTab - Whether the action was initiated from the overview tab
 */
export const trackStartVideoTo911ButtonClick = ({
  fromOverviewTab = false,
}) => {
  gspx.trackEvent({
    name: VT911_EVENT_NAMES.START,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    additionalKeys: {
      overview_tab: fromOverviewTab,
    },
  });
};

/**
 * Tracks the response when initiating a VT911 session
 * @param {Object} params - The parameters for tracking the video session response
 * @param {boolean} params.responseSuccess - Whether the response was successful
 * @param {string} params.roomId - The video room ID
 */
export const trackStartVT911Response = ({ responseSuccess, roomId }) => {
  gspx.trackEvent({
    name: VT911_EVENT_NAMES.START_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
    additionalKeys: {
      success: responseSuccess,
    },
  });
};

/**
 * Tracks when a user ends a VT911 session
 * @param {Object} params - The parameters for tracking the end session action
 * @param {string} params.roomId - The video room ID
 */
export const trackEndVT911Click = ({ roomId }) => {
  gspx.trackEvent({
    name: VT911_EVENT_NAMES.END,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
  });
};

/**
 * Tracks the response when ending a VT911 session
 * @param {Object} params - The parameters for tracking the video session response
 * @param {boolean} params.responseSuccess - Whether the response was successful
 * @param {string} params.roomId - The video room ID
 */
export const trackEndVT911Response = ({ responseSuccess, roomId }) => {
  gspx.trackEvent({
    name: VT911_EVENT_NAMES.END_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
    additionalKeys: {
      success: responseSuccess,
    },
  });
};

/**
 * Tracks when a VT911 session is ended by the server
 * @param {Object} params - The parameters for tracking the server-ended session
 * @param {string} params.roomId - The ID of the room that was ended
 */
export const trackEndVT911FromServer = ({ roomId }) => {
  gspx.trackEvent({
    name: VT911_EVENT_NAMES.END_FROM_SERVER,
    type: GPX_EVENT_TYPES.API_PUSH,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
  });
};

/**
 * Tracks when a user clicks to blur or unblur a live video
 * @param {Object} params - The parameters for tracking the blur/unblur action
 * @param {boolean} params.blurVideo - True for blur, false for unblur
 * @param {string} params.roomId - The ID of the video room
 */
export const trackVideoBlurUnblurClick = ({ blurVideo, roomId }) => {
  gspx.trackEvent({
    name: blurVideo
      ? VT911_EVENT_NAMES.BLUR_VIDEO
      : VT911_EVENT_NAMES.UNBLUR_VIDEO,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
  });
};

/**
 * Tracks when a user clicks to toggle fullscreen on a live video
 * @param {Object} params - The parameters for tracking the toggle fullscreen action
 * @param {boolean} params.triggerFullscreen - True for show fullscreen, false for hide fullscreen
 * @param {string} params.roomId - The ID of the video room
 */
export const trackVideoToggleFullscreenClick = ({
  triggerFullscreen,
  roomId,
}) => {
  gspx.trackEvent({
    name: triggerFullscreen
      ? VT911_EVENT_NAMES.SHOW_FULLSCREEN
      : VT911_EVENT_NAMES.HIDE_FULLSCREEN,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
  });
};

/**
 * Tracks when a user clicks to mute or unmute a live video
 * @param {Object} params - The parameters for tracking the mute/unmute action
 * @param {boolean} params.muteVideo - True for mute, false for unmute
 * @param {string} params.roomId - The ID of the video room
 */
export const trackVideoMuteUnmuteClick = ({ muteVideo, roomId }) => {
  gspx.trackEvent({
    name: muteVideo
      ? VT911_EVENT_NAMES.MUTE_VIDEO
      : VT911_EVENT_NAMES.UNMUTE_VIDEO,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      room_id: roomId,
    },
  });
};

export const trackHideVideoCard = () => {
  gspx.trackEvent({
    name: VT911_EVENT_NAMES.HIDE_VIDEO_CARD,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.VT911,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.COMMUNICATOR,
  });
};
